import React, {useCallback, useEffect, useRef, useState} from "react";
import {ColumnFilterProps} from "./types";
import {CountrySelectV2} from "@/lib/countries/CountrySelectV2";
import {FacilitySelectV2} from "@/lib/facilities/FacilitySelectV2";
import {ButtonV2} from "@/components/ui/ButtonsV2/ButtonV2";
import {useDropdownPosition} from "@/hooks/useDropdownPosition";

type LocationType = "" | "country" | "facility";

type Country = {
    countryCode: string;
    countryNameEn: string;
    flag: string;
};

type Facility = {
    id: string;
    name: string;
    clientID: string;
    clientName: string;
    address: {
        formatted: string;
    };
};

export const LocationTypeFilter: React.FC<ColumnFilterProps> = ({label, onChange, filterParams}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const position = useDropdownPosition(isOpen, dropdownRef, 300);

    // Initialize from filterParams
    const getInitialLocationType = useCallback((): LocationType => {
        if (filterParams.locationType) {
            return filterParams.locationType as LocationType;
        }
        return "";
    }, [filterParams.locationType]);

    const getInitialCountry = useCallback((): Country | undefined => {
        if (filterParams.countryISO && filterParams.countryName) {
            return {
                countryCode: filterParams.countryISO as string,
                countryNameEn: filterParams.countryName as string,
                flag: "",
            };
        }
        return undefined;
    }, [filterParams.countryISO, filterParams.countryName]);

    const getInitialFacility = useCallback((): Facility | undefined => {
        if (filterParams.facilityID && filterParams.facilityName) {
            return {
                id: filterParams.facilityID as string,
                name: filterParams.facilityName as string,
                clientID: "",
                clientName: "",
                address: { formatted: "" },
            };
        }
        return undefined;
    }, [filterParams.facilityID, filterParams.facilityName]);

    // Applied state (what's currently in the filter)
    const [appliedLocationType, setAppliedLocationType] = useState<LocationType>(getInitialLocationType);
    const [appliedCountry, setAppliedCountry] = useState<Country | undefined>(getInitialCountry);
    const [appliedFacility, setAppliedFacility] = useState<Facility | undefined>(getInitialFacility);

    // Staged state (what the user is selecting)
    const [locationType, setLocationType] = useState<LocationType>(getInitialLocationType);
    const [selectedCountry, setSelectedCountry] = useState<Country | undefined>(getInitialCountry);
    const [selectedFacility, setSelectedFacility] = useState<Facility | undefined>(getInitialFacility);

    const handleToggle = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        if (!isOpen) {
            // When opening, sync staged state with applied state
            setLocationType(appliedLocationType);
            setSelectedCountry(appliedCountry);
            setSelectedFacility(appliedFacility);
        }
        setIsOpen(!isOpen);
    }, [isOpen, appliedLocationType, appliedCountry, appliedFacility]);

    const handleSelectType = useCallback((type: LocationType) => {
        setLocationType(type);
        // Clear specific selections when changing type
        setSelectedCountry(undefined);
        setSelectedFacility(undefined);
    }, []);

    const handleCountryChange = useCallback((country: Country | undefined) => {
        setSelectedCountry(country);
    }, []);

    const handleFacilityChange = useCallback((facility: Facility | undefined) => {
        setSelectedFacility(facility);
    }, []);

    const handleApply = useCallback(() => {
        // Apply the staged selections
        setAppliedLocationType(locationType);
        setAppliedCountry(selectedCountry);
        setAppliedFacility(selectedFacility);

        // Update filter params
        if (locationType === "") {
            onChange({
                locationType: undefined,
                countryISO: undefined,
                countryName: undefined,
                facilityID: undefined,
                facilityName: undefined,
            });
        } else if (locationType === "country") {
            if (selectedCountry) {
                onChange({
                    locationType: "country",
                    countryISO: selectedCountry.countryCode,
                    countryName: selectedCountry.countryNameEn,
                    facilityID: undefined,
                    facilityName: undefined,
                });
            } else {
                onChange({
                    locationType: "country",
                    countryISO: undefined,
                    countryName: undefined,
                    facilityID: undefined,
                    facilityName: undefined,
                });
            }
        } else if (locationType === "facility") {
            if (selectedFacility) {
                onChange({
                    locationType: "facility",
                    facilityID: selectedFacility.id,
                    facilityName: selectedFacility.name,
                    countryISO: undefined,
                    countryName: undefined,
                });
            } else {
                onChange({
                    locationType: "facility",
                    facilityID: undefined,
                    facilityName: undefined,
                    countryISO: undefined,
                    countryName: undefined,
                });
            }
        }
        setIsOpen(false);
    }, [locationType, selectedCountry, selectedFacility, onChange]);

    const handleClear = useCallback(() => {
        setLocationType("");
        setSelectedCountry(undefined);
        setSelectedFacility(undefined);
        setAppliedLocationType("");
        setAppliedCountry(undefined);
        setAppliedFacility(undefined);
        onChange({
            locationType: undefined,
            countryISO: undefined,
            countryName: undefined,
            facilityID: undefined,
            facilityName: undefined,
        });
    }, [onChange]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    // Update state when filterParams change (e.g., from URL)
    useEffect(() => {
        const newLocationType = getInitialLocationType();
        const newCountry = getInitialCountry();
        const newFacility = getInitialFacility();

        setAppliedLocationType(newLocationType);
        setAppliedCountry(newCountry);
        setAppliedFacility(newFacility);

        setLocationType(newLocationType);
        setSelectedCountry(newCountry);
        setSelectedFacility(newFacility);
    }, [getInitialLocationType, getInitialCountry, getInitialFacility]);

    const getDisplayText = () => {
        if (appliedLocationType === "country") {
            return appliedCountry ? `Country (${appliedCountry.countryNameEn})` : "Country";
        } else if (appliedLocationType === "facility") {
            return appliedFacility ? `Facility (${appliedFacility.name})` : "Facility";
        }
        return null;
    };

    const displayText = getDisplayText();

    return (
        <div className="relative inline-block" ref={dropdownRef}>
            <button
                onClick={handleToggle}
                className="flex items-center gap-1 hover:text-blue-600 transition-colors font-bold"
            >
                <span>{label}</span>
                {displayText && (
                    <span className="text-xs text-blue-600 font-normal">
                        ({displayText})
                    </span>
                )}
                <span className={`text-xs transition-transform ${isOpen ? "rotate-180" : ""}`}>▼</span>
            </button>

            {isOpen && (
                <div className={`absolute ${position} mt-1 bg-white rounded shadow-lg z-50 min-w-[300px] flex flex-col`} onClick={(e) => e.stopPropagation()}>
                    {/* Location type selection */}
                    <div className="p-3 border-b">
                        <div className="space-y-2">
                            <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                                <input
                                    type="radio"
                                    name="locationType"
                                    checked={locationType === ""}
                                    onChange={() => handleSelectType("")}
                                    className="cursor-pointer"
                                />
                                <span className="font-normal">All</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                                <input
                                    type="radio"
                                    name="locationType"
                                    checked={locationType === "country"}
                                    onChange={() => handleSelectType("country")}
                                    className="cursor-pointer"
                                />
                                <span className="font-normal">Country</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                                <input
                                    type="radio"
                                    name="locationType"
                                    checked={locationType === "facility"}
                                    onChange={() => handleSelectType("facility")}
                                    className="cursor-pointer"
                                />
                                <span className="font-normal">Facility</span>
                            </label>
                        </div>
                    </div>

                    {/* Country selector */}
                    {locationType === "country" && (
                        <div className="p-3 border-b">
                            <CountrySelectV2
                                label="Select Country"
                                onChange={handleCountryChange}
                                initialValue={selectedCountry?.countryCode}
                            />
                        </div>
                    )}

                    {/* Facility selector */}
                    {locationType === "facility" && (
                        <div className="p-3 border-b">
                            <FacilitySelectV2
                                label="Select Facility"
                                clientID={filterParams.clientID as string | undefined}
                                onChange={handleFacilityChange}
                                initialID={selectedFacility?.id}
                            />
                        </div>
                    )}

                    {/* Action buttons */}
                    <div className="p-3 flex gap-2">
                        <div className="flex-1">
                            <ButtonV2 label="Clear" color="gray" onClick={handleClear} />
                        </div>
                        <div className="flex-1">
                            <ButtonV2 label="Apply Filter" color="blue" onClick={handleApply} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
